import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DocumentTextIcon,
  EyeIcon,
} from '../icons';
import { courtAPI, transcriptionAPI } from '../services/api';
import CaseReferenceLink from '../components/cases/CaseReferenceLink';
import JobStatusBadge from '../components/transcription/JobStatusBadge';
import PageLoadingState from '../components/layout/PageLoadingState';
import MatDashTableCard from '../components/layout/MatDashTableCard';
import {
  buildCasesFromTranscriptions,
  findCaseByRouteId,
  normalizeCourtCase,
  transcriptionMatchesCase,
} from '../utils/courtCases';
import {
  formatDate,
  formatDuration,
  parseTranscriptionRecord,
} from '../utils/transcriptionMeta';

const CourtCaseDetail = () => {
  const { id: routeId } = useParams();
  const [courtCase, setCourtCase] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setUsingFallback(false);

      let resolvedCase = null;

      try {
        const response = await courtAPI.getCourtCase(decodeURIComponent(routeId));
        resolvedCase = normalizeCourtCase(response.data?.data || response.data);
      } catch {
        setUsingFallback(true);
        try {
          const txResp = await transcriptionAPI.getTranscriptions({ page: 1, per_page: 500 });
          const list = (txResp.data?.data || txResp.data || []).map(parseTranscriptionRecord);
          resolvedCase = findCaseByRouteId(buildCasesFromTranscriptions(list), routeId);
        } catch (err) {
          if (!cancelled) {
            setError(err.response?.data?.message || err.message || 'Failed to load case');
            setLoading(false);
          }
          return;
        }
      }

      if (!resolvedCase) {
        if (!cancelled) {
          setError('Case not found.');
          setCourtCase(null);
          setSessions([]);
          setLoading(false);
        }
        return;
      }

      if (!cancelled) setCourtCase(resolvedCase);

      try {
        const txResp = await transcriptionAPI.getTranscriptions({ page: 1, per_page: 500 });
        const list = (txResp.data?.data || txResp.data || [])
          .map(parseTranscriptionRecord)
          .filter((t) => transcriptionMatchesCase(t, resolvedCase));
        if (!cancelled) setSessions(list);
      } catch {
        if (!cancelled) setSessions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [routeId]);

  const sessionStats = useMemo(() => {
    const complete = sessions.filter(
      (s) => s.jobStatus === 'complete' || s.status === 'completed'
    ).length;
    return { total: sessions.length, complete, processing: sessions.length - complete };
  }, [sessions]);

  if (loading) {
    return <PageLoadingState message="Loading case…" />;
  }

  if (error || !courtCase) {
    return (
      <>
        <div className="md-alert md-alert-danger">{error || 'Case not found.'}</div>
      </>
    );
  }

  return (
    <>
      {usingFallback && (
        <div className="md-alert-warning mb-4">
          Case details inferred from linked session records.
        </div>
      )}

      <div className="md-card md-card-body mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <p className="text-sm text-matdash-muted mb-1">Docket</p>
            <h1 className="text-2xl font-bold text-matdash-dark">{courtCase.docket_number}</h1>
            <p className="text-lg text-matdash-muted mt-1">{courtCase.title}</p>
          </div>
          <span className="inline-flex px-3 py-1 rounded-md text-sm font-medium capitalize bg-matdash-primary-subtle text-matdash-primary self-start">
            {courtCase.status}
          </span>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-matdash-border text-sm">
          <div>
            <dt className="text-matdash-muted">Filed</dt>
            <dd className="font-semibold text-matdash-dark mt-0.5">
              {formatDate(courtCase.created_at) || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-matdash-muted">Court</dt>
            <dd className="font-semibold text-matdash-dark mt-0.5">
              {courtCase.court_name || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-matdash-muted">Sessions</dt>
            <dd className="font-semibold text-matdash-dark mt-0.5">
              {sessionStats.total} total · {sessionStats.complete} complete
            </dd>
          </div>
        </dl>

        {courtCase.description && (
          <p className="mt-4 text-sm text-matdash-muted border-t border-matdash-border pt-4">
            {courtCase.description}
          </p>
        )}
      </div>

      <MatDashTableCard
        title="Linked transcriptions"
        subtitle={`${sessions.length} session${sessions.length !== 1 ? 's' : ''} for this docket`}
      >
        {sessions.length > 0 ? (
          <>
            <div className="block sm:hidden divide-y divide-matdash-border">
              {sessions.map((session) => (
                <div key={session.id} className="p-4">
                  <p className="font-medium text-matdash-dark text-sm">{session.title}</p>
                  <div className="flex justify-between items-center mt-2">
                    <JobStatusBadge status={session.status} className="text-xs" />
                    <Link to={`/transcriptions/${session.id}`} className="md-link text-sm">
                      View
                    </Link>
                  </div>
                  <p className="text-xs text-matdash-muted mt-1">
                    {formatDate(session.created_at)} · {formatDuration(session.duration)}
                  </p>
                </div>
              ))}
            </div>

            <div className="hidden sm:block md-table-wrap">
              <table className="md-table">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Case ref</th>
                    <th>Date</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.id}>
                      <td className="font-medium">{session.title}</td>
                      <td>
                        <CaseReferenceLink
                          caseReference={session.case_reference}
                          caseId={courtCase.id}
                        />
                      </td>
                      <td className="text-matdash-muted">{formatDate(session.created_at)}</td>
                      <td className="text-matdash-muted">{formatDuration(session.duration)}</td>
                      <td><JobStatusBadge status={session.status} /></td>
                      <td className="text-right">
                        <Link
                          to={`/transcriptions/${session.id}`}
                          className="md-link inline-flex p-1"
                          title="View transcription"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="md-empty-state">
            <DocumentTextIcon className="h-12 w-12 text-matdash-border mx-auto mb-3" />
            <p className="font-semibold text-matdash-dark">No linked transcriptions yet</p>
            <p className="text-sm mt-1">
              Start a recording or upload audio with this docket number.
            </p>
            <Link to="/dashboard" className="md-link inline-block mt-4 text-sm">
              Go to Dashboard →
            </Link>
          </div>
        )}
      </MatDashTableCard>
    </>
  );
};

export default CourtCaseDetail;
